/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import {isRef, unref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {AxiosResponse} from 'axios';
import {saveAs} from 'file-saver';
import * as XLSX from 'xlsx';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface ServerResponse {
  data?: any;
  meta?: any;
  error?: boolean;
  message?: string;
}

interface DTO {
  [key: string]: any;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

async function fetchData(
  http: APIService,
  params: object,
): Promise<ServerResponse> {
  try {
    const response: AxiosResponse = await http.getAll(params);
    return {
      data: response.data.data,
      meta: response.data.meta,
      error: false,
    };
  } catch (error) {
    return {
      error: true,
      message: (error as Error).message,
    };
  }
}

function getPageParams(pageSize: number, currentPage: number) {
  const offset = pageSize * (currentPage - 1);
  return {
    limit: pageSize,
    offset,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getQueryParams(query: any) {
  const params = JSON.parse(JSON.stringify(unref(query)));
  for (const [key, value] of Object.entries(params)) {
    params[key] = value === null || value === '' ? undefined : value;
  }
  return params;
}

/* Override to mutate fields after fetching */
function defaultNormalizer(data: DTO[]): DTO[] {
  return data;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type usePaginateArgs = {
  query?: object;
  normalizer?: (data: DTO[]) => DTO[];
  pageSize?: number;
  pages?: any;
  filename?: string;
};

export default function useExport(
  http: APIService,
  {
    query = {},
    normalizer = defaultNormalizer,
    pageSize = 10000, // Removing pagination for time being.
    pages = 0,
    filename = 'data',
  }: usePaginateArgs = {},
) {
  const execQuery = async () => {
    if (isRef(pages)) {
      pages = pages.value;
    }
    pages = 1; // Removing pagination for time being.
    const dataExport: any[] = [];
    for (let currentPage = 1; currentPage <= pages; currentPage++) {
      const pageParams = getPageParams(pageSize, currentPage);
      const queryParams = getQueryParams(query);
      const response = await fetchData(http, {...pageParams, ...queryParams});
      if (!response.error) {
        const data = response.data;
        const formattedData = normalizer(data);
        formattedData.forEach((item) => {
          dataExport.push(item);
        });
      }
    }
    if (!dataExport) {
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(dataExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    const blob = new Blob([excelBuffer], {type: 'application/octet-stream'});
    saveAs(blob, `${filename}.xlsx`);
    /*let csv = unparse(
      dataExport,
      Object.assign(
        {
          delimiter: ',',
          encoding: 'utf-8',
        },
        {},
      ),
    );
    csv = '\ufeff' + csv;
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8',
    });
    saveAs(blob, filename);*/
  };

  return {
    execQuery,
  };
}
